import { GridFilterItem } from '@mui/x-data-grid-pro';

import { FilterState } from '../../components/molecules/FilterBar';
import { FILTER_FIELD_DEFINITIONS } from '../../components/molecules/FilterBar/AddFilterModal.types';
import { advancedFiltersFromModel } from '../../utils/advancedFilterFromModel';

/** Optional lookup maps passed to `filterModelToFilterState` so it can
 *  convert BFF name-based predicates (from saved reports / URL state) back
 *  into the code/id-based FilterState values the dropdown uses.
 *  When absent the function stores values as-is — the `buildFilterModel`
 *  fallback emits them unchanged, preserving backward compat. */
export interface FilterStateConversionLookups {
  /** status name → all codes that share that name */
  statusNameToCodes?: Record<string, string[]>;
  /** status code → display name */
  statusCodeToName?: Record<string, string>;
  /** client name → id string */
  clientNameToId?: Record<string, string>;
  /** client id string → display name */
  clientIdToName?: Record<string, string>;
}

/** Convert a GridFilterModel back into a FilterState for the FilterBar display. */
export const filterModelToFilterState = (
  model: { items: GridFilterItem[] },
  lookups?: FilterStateConversionLookups
): FilterState => {
  const state: FilterState = {
    statuses: [],
    jobTypes: [],
    contracts: [],
    contractNames: [],
    extraText: '',
    jobId: '',
    multiSearch: '',
    advancedFilters: [],
  };

  // Predicates that don't belong to a quick-filter slot become Add Filter
  // chips; `advancedFiltersFromModel` owns that reconstruction.
  const rest: GridFilterItem[] = [];

  for (const item of model.items) {
    if (
      item.field === 'serviceStatusName' &&
      item.operator === 'isAnyOf' &&
      Array.isArray(item.value)
    ) {
      const names = item.value.map(String);
      if (lookups?.statusNameToCodes && lookups.statusCodeToName) {
        // Convert each name to all matching codes so every dropdown option
        // for that name appears ticked in the FilterBar.
        state.statusCodeToName ??= {};
        for (const name of names) {
          const codes = lookups.statusNameToCodes[name];
          if (codes?.length) {
            state.statuses.push(...codes);
            for (const code of codes) state.statusCodeToName[code] = name;
          } else {
            state.statuses.push(name);
          }
        }
      } else {
        state.statuses.push(...names);
      }
    } else if (item.field === 'serviceStatusName' && item.operator === 'equals' && item.value) {
      const name = String(item.value);
      if (lookups?.statusNameToCodes && lookups.statusCodeToName) {
        state.statusCodeToName ??= {};
        const codes = lookups.statusNameToCodes[name];
        if (codes?.length) {
          state.statuses.push(...codes);
          for (const code of codes) state.statusCodeToName[code] = name;
        } else {
          state.statuses.push(name);
        }
      } else {
        state.statuses.push(name);
      }
    } else if (
      item.field === 'serviceTypeName' &&
      item.operator === 'isAnyOf' &&
      Array.isArray(item.value)
    ) {
      state.jobTypes.push(...item.value.map(String));
    } else if (item.field === 'serviceTypeName' && item.operator === 'equals' && item.value) {
      state.jobTypes.push(String(item.value));
    } else if (
      item.field === 'contractName' &&
      item.operator === 'isAnyOf' &&
      Array.isArray(item.value)
    ) {
      // Only the set-membership operators map back into the Contract quick
      // filter. `contractName` is also offered in the Add Filter modal, where
      // `not` is a valid choice (OPS_LIST is isAnyOf | not) — those must fall
      // through to the advancedFilters branch so they keep their operator and
      // round-trip as their own chip rather than being flattened into the
      // dropdown's implicit "is any of".
      state.contractNames.push(...item.value.map(String));
    } else if (item.field === 'contractName' && item.operator === 'equals' && item.value) {
      state.contractNames.push(String(item.value));
    } else if (
      item.field === 'clientName' &&
      item.operator === 'isAnyOf' &&
      Array.isArray(item.value)
    ) {
      const names = item.value.map(String);
      if (lookups?.clientNameToId && lookups.clientIdToName) {
        state.contractIdToName ??= {};
        for (const name of names) {
          const id = lookups.clientNameToId[name];
          if (id !== undefined) {
            state.contracts.push(id);
            state.contractIdToName[id] = name;
          } else {
            state.contracts.push(name);
          }
        }
      } else {
        state.contracts.push(...names);
      }
    } else if (item.field === 'clientName' && item.operator === 'equals' && item.value) {
      const name = String(item.value);
      if (lookups?.clientNameToId && lookups.clientIdToName) {
        state.contractIdToName ??= {};
        const id = lookups.clientNameToId[name];
        if (id !== undefined) {
          state.contracts.push(id);
          state.contractIdToName[id] = name;
        } else {
          state.contracts.push(name);
        }
      } else {
        state.contracts.push(name);
      }
    } else if (
      item.field === 'clientCode' &&
      item.operator === 'isAnyOf' &&
      Array.isArray(item.value)
    ) {
      // Legacy format: saved reports written before the dropdown switched to
      // name-based values. Tag each code so buildFilterModel re-emits the
      // original clientCode predicate and the report keeps returning the
      // same rows on re-search.
      const values = item.value.map(String);
      state.contracts.push(...values);
      state.contractsLegacyCode ??= {};
      for (const v of values) state.contractsLegacyCode[v] = 'isAnyOf';
    } else if (item.field === 'clientCode' && item.operator === 'equals' && item.value) {
      const v = String(item.value);
      state.contracts.push(v);
      state.contractsLegacyCode ??= {};
      state.contractsLegacyCode[v] = 'equals';
    } else if (
      item.field === 'clientHierarchy' &&
      item.operator === 'isAnyOf' &&
      Array.isArray(item.value)
    ) {
      // Pre-rebuild reports stored Client picks against `clientHierarchy`;
      // the new dropdown emits `clientCode`. Both are surfaced through the
      // same dropdown slot so the saved value still ticks the dropdown
      // rather than rendering as an orphan chip. We tag each hierarchy
      // value in `contractsLegacyHierarchy` so `buildFilterModel` re-emits
      // it as the original `clientHierarchy isAnyOf` predicate on search —
      // critical because hierarchy values are often substrings/path
      // segments, not real client codes (e.g. `HOTS`), and converting them
      // to `clientCode equals` would silently return zero rows.
      const values = item.value.map(String);
      state.contracts.push(...values);
      state.contractsLegacyHierarchy ??= {};
      for (const v of values) state.contractsLegacyHierarchy[v] = 'isAnyOf';
    } else if (
      item.field === 'clientHierarchy' &&
      (item.operator === 'equals' || item.operator === 'is' || item.operator === 'contains') &&
      item.value
    ) {
      const v = String(item.value);
      state.contracts.push(v);
      state.contractsLegacyHierarchy ??= {};
      state.contractsLegacyHierarchy[v] = item.operator;
    } else if (item.field === 'serviceId' && item.operator === 'isAnyOf' && item.value) {
      // Only the quick "Job ID" search box maps back into the dedicated jobId
      // slot — it always emits `serviceId isAnyOf`. A custom "Job ID" filter
      // added via the Add Filter modal uses `equals`/`contains`; those must
      // fall through to the advancedFilters branch below so they round-trip as
      // their own chip (and keep their operator) instead of being silently
      // swallowed into the quick-filter slot.
      state.jobId = String(item.value);
    } else {
      rest.push(item);
    }
  }

  state.advancedFilters = advancedFiltersFromModel(rest, FILTER_FIELD_DEFINITIONS);

  return state;
};
